import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import {
  addComment,
  applyEdit,
  clearMark,
  clearMarks,
  goToEdit,
  goToParagraph,
  markDocument,
  readParagraphs,
  supportsReview,
} from "./officeBridge";
import { fetchReviewRules, isAuthError, reviewBrief } from "./wordAddinApi";
import {
  EDIT_KINDS,
  NOTE_KINDS,
  commentText,
  countByKind,
  itemLabel,
  marksFor,
  paragraphRefs,
  paragraphsForReview,
  reviewHeadline,
  visibleEdits,
} from "./wordAddinModel";
import { Button, Notice, Primary, Section, SectionTitle, Small } from "./paneStyles";

/*
 * "Review my brief": the AI VA Attorney reads the open draft against the
 * firm's style sheet. Nothing in the document changes until the reader
 * chooses to — "Mark all" pins every suggestion to its words as a numbered
 * comment (and takes them out again), a note becomes a margin comment, a
 * line edit becomes a tracked change, and anything can be dismissed instead.
 */

const Card = styled.div`
  border: 1px solid #e2e8f0;
  border-left: 3px solid ${(p) => p.$accent || "#94a3b8"};
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: ${(p) => (p.$done ? "#f8fafc" : "#fff")};
`;

const Kind = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  button {
    padding: 5px 8px;
    font-size: 11px;
  }
`;

const Chips = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const Chip = styled.button`
  border: 1px solid ${(p) => (p.$active ? "#1e40af" : "#cbd5e1")};
  background: ${(p) => (p.$active ? "#dbeafe" : "#fff")};
  color: #0f172a;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
`;

const Was = styled.div`
  color: #991b1b;
  text-decoration: line-through;
  font-size: 12px;
  line-height: 1.4;
`;

const Now = styled.div`
  color: #065f46;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.4;
`;

const Rules = styled.div`
  h4 {
    margin: 8px 0 2px;
    font-size: 12px;
  }
  ul {
    margin: 0;
    padding-left: 16px;
  }
  li {
    font-size: 12px;
    line-height: 1.4;
    margin-bottom: 2px;
  }
`;

// A paragraph number the reviewer mentions, as a link that selects it in Word.
const Ref = styled.button`
  border: 0;
  background: #c7d2fe;
  color: #1e1b4b;
  border-radius: 999px;
  padding: 0 7px;
  margin: 0 1px;
  font: inherit;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.5;
  cursor: pointer;
  vertical-align: baseline;
`;

const ACCENT = { structure: "#7c3aed", voice: "#d97706", words: "#0891b2", style: "#64748b", passive: "#d97706", clarity: "#2563eb" };

// Text with every "[n]" turned into a link to that paragraph.
const Linked = ({ text, onRef, testid }) => (
  <span data-testid={testid}>
    {paragraphRefs(text).map((part, i) =>
      part.ref !== undefined ? (
        <Ref key={i} type="button" onClick={() => onRef(part.ref)} title={`Show paragraph ${part.ref} in the document`} data-testid="addin-ref">
          ¶{part.ref}
        </Ref>
      ) : (
        <React.Fragment key={i}>{part.text}</React.Fragment>
      )
    )}
  </span>
);

const BriefReviewTab = ({ filename, say, onAuthError }) => {
  const [review, setReview] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [kind, setKind] = useState("all");
  const [hidden, setHidden] = useState(() => new Set());
  const [done, setDone] = useState({});
  const [busyId, setBusyId] = useState("");
  const [marked, setMarked] = useState(0);
  const [marking, setMarking] = useState(false);
  const [rules, setRules] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const [canWrite] = useState(() => supportsReview());

  const totals = useMemo(
    () => ({ notes: (review?.notes || []).length, edits: (review?.edits || []).length }),
    [review]
  );

  // Our comments are out of the document: no note is "commented" any more;
  // applied edits stay applied.
  const forgetComments = useCallback(() => {
    setMarked(0);
    setDone((d) => Object.fromEntries(Object.entries(d).filter(([, state]) => state !== "commented")));
  }, []);

  const runReview = useCallback(async () => {
    setRunning(true);
    setError("");
    try {
      // A fresh review supersedes the comments of the last one; take them out
      // first so the document does not carry two sets of numbers. If Word
      // refuses, keep "Remove marks" available.
      let removed = 0;
      if (marked) {
        try {
          removed = await clearMarks();
          forgetComments();
        } catch (err) {
          removed = 0;
        }
      }
      const paragraphs = await readParagraphs();
      const res = await reviewBrief({ filename, paragraphs: paragraphsForReview(paragraphs) });
      setReview(res.data);
      setHidden(new Set());
      setDone({});
      setKind("all");
      if (removed) say(`Removed ${removed} comment${removed === 1 ? "" : "s"} from the earlier review.`);
    } catch (err) {
      if (isAuthError(err)) {
        onAuthError();
      } else if (err?.code === "ECONNABORTED") {
        setError("The review took longer than five minutes and was stopped. Try again, or review a shorter section.");
      } else {
        setError(err?.response?.data?.error || err?.message || "WHG Docs did not answer.");
      }
    } finally {
      setRunning(false);
    }
  }, [filename, forgetComments, marked, onAuthError, say]);

  const apply = useCallback(
    async (edit) => {
      setBusyId(edit.id);
      try {
        await applyEdit(edit);
        setDone((d) => ({ ...d, [edit.id]: "applied" }));
        if (marked) await clearMark(itemLabel(edit, totals)).catch(() => {});
        say("Applied as a tracked change — accept or reject it in Word's Review pane.");
      } catch (err) {
        say(err.message || "Could not make that change.", "warn");
      } finally {
        setBusyId("");
      }
    },
    [marked, say, totals]
  );

  const comment = useCallback(
    async (note) => {
      setBusyId(note.id);
      try {
        // Never two comments for one note: drop any earlier copy of ours first.
        if (marked) await clearMark(itemLabel(note, totals)).catch(() => {});
        await addComment({ paragraph: note.anchor, quote: note.quote, text: commentText(note, totals) });
        setDone((d) => ({ ...d, [note.id]: "commented" }));
        setMarked((m) => m + 1);
        say("Comment added in the margin.");
      } catch (err) {
        say(err.message || "Could not add the comment.", "warn");
      } finally {
        setBusyId("");
      }
    },
    [marked, say, totals]
  );

  const goTo = useCallback(
    async (index) => {
      try {
        await goToParagraph(index);
      } catch (err) {
        say(err.message || "Could not find that paragraph.", "warn");
      }
    },
    [say]
  );

  const showEdit = useCallback(
    async (edit) => {
      try {
        await goToEdit(edit);
      } catch (err) {
        say(err.message || "Could not find that text.", "warn");
      }
    },
    [say]
  );

  const markAll = useCallback(async () => {
    setMarking(true);
    try {
      const marks = marksFor(review, { hidden, done });
      const { placed, missed, placedIds = [] } = await markDocument(marks);
      setMarked(placed);
      // Every note now has its comment in the document (or lost it, if its
      // place could not be found) — the cards say so instead of offering a
      // second "Add comment".
      setDone((d) => {
        const next = { ...d };
        for (const note of review?.notes || []) {
          if (placedIds.includes(note.id)) next[note.id] = "commented";
          else if (next[note.id] === "commented") delete next[note.id];
        }
        return next;
      });
      const where = "Step through them with Next and Previous on Word's Review ribbon.";
      if (missed.length) {
        say(`${placed} comments placed. ${where} Could not find the text for ${missed.join(", ")}.`, "warn");
      } else {
        say(`${placed} comments placed in the document. ${where}`);
      }
    } catch (err) {
      say(err.message || "Could not mark the document.", "warn");
    } finally {
      setMarking(false);
    }
  }, [done, hidden, review, say]);

  const unmark = useCallback(async () => {
    setMarking(true);
    try {
      const removed = await clearMarks();
      forgetComments();
      say(removed ? `Removed ${removed} review comment${removed === 1 ? "" : "s"}. Nothing else was touched.` : "No review comments to remove.");
    } catch (err) {
      say(err.message || "Could not remove the marks.", "warn");
    } finally {
      setMarking(false);
    }
  }, [forgetComments, say]);

  const dismiss = useCallback((id) => setHidden((h) => new Set([...h, id])), []);

  const toggleRules = useCallback(async () => {
    if (!rules) {
      try {
        const res = await fetchReviewRules();
        setRules(res.data);
      } catch (err) {
        if (isAuthError(err)) onAuthError();
        else say("Could not load the rules.", "warn");
        return;
      }
    }
    setShowRules((s) => !s);
  }, [onAuthError, rules, say]);

  // One write to the document at a time — and none while a fresh review is
  // being read, when the cards on screen are about to be replaced.
  const locked = !canWrite || running || marking || Boolean(busyId);
  const counts = countByKind(review?.edits);
  const notes = (review?.notes || []).filter((n) => !hidden.has(n.id));
  const edits = visibleEdits(review?.edits, { kind, hidden });

  return (
    <>
      <Section data-testid="addin-review">
        <SectionTitle>Review my brief</SectionTitle>
        <Small>
          Reads the whole draft against the firm&apos;s style sheet: word choice, passive voice, and arguments that
          start, break off and come back. Suggestions appear here; nothing in the document changes until you apply
          one.
        </Small>
        <Primary type="button" onClick={runReview} disabled={running || marking || Boolean(busyId)} data-testid="addin-review-run">
          {running ? "Reading the brief… about a minute" : review ? "Review again" : "Review my brief"}
        </Primary>
        {!canWrite && (
          <Notice $bg="#fffbeb" $fg="#92400e">
            This Word can&apos;t take tracked changes or comments from the panel; open the file in Word on the web to
            apply suggestions.
          </Notice>
        )}
        {error && (
          <Notice $bg="#fef2f2" $fg="#991b1b" data-testid="addin-review-error">
            {error}
          </Notice>
        )}
        <Button type="button" onClick={toggleRules} style={{ alignSelf: "flex-start" }} data-testid="addin-review-rules">
          {showRules ? "Hide the rules" : "Rules it applies"}
        </Button>
        {showRules && rules && (
          <Rules data-testid="addin-review-rules-body">
            <Small>Style sheet version {rules.version}. Tell Matthew what to strike or add.</Small>
            {(rules.sections || []).map((section) => (
              <div key={section.heading}>
                <h4>{section.heading}</h4>
                <ul>
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </Rules>
        )}
      </Section>

      {review && (
        <>
          <Section data-testid="addin-review-summary">
            <Small data-testid="addin-review-headline">{reviewHeadline(review)}</Small>
            {review.summary && (
              <Notice $bg="#eef2ff" $fg="#1e1b4b">
                <Linked text={review.summary} onRef={goTo} testid="addin-review-summary-text" />
              </Notice>
            )}
            {(review.degraded || []).length > 0 && (
              <Notice $bg="#fffbeb" $fg="#92400e" data-testid="addin-review-degraded">
                Part of the review did not come back: {review.degraded.join("; ")}.
              </Notice>
            )}
            <Actions>
              <Primary type="button" onClick={markAll} disabled={locked} data-testid="addin-review-mark-all">
                {marking ? "Working…" : marked ? "Mark again" : "Mark all in the document"}
              </Primary>
              {marked > 0 && (
                <Button type="button" onClick={unmark} disabled={locked} data-testid="addin-review-unmark">
                  Remove marks
                </Button>
              )}
            </Actions>
            <Small>
              Mark all pins every suggestion below to its words as a numbered comment, so you can read them in the
              document and step through them with Next on Word&apos;s Review ribbon. Remove marks takes out only those
              comments. A ¶ number is a link to that paragraph.
            </Small>
          </Section>

          <Section>
            <SectionTitle>Notes for the margin</SectionTitle>
            {notes.length === 0 && <Small>No notes.</Small>}
            {notes.map((note) => (
              <Card key={note.id} $accent={ACCENT[note.kind]} $done={Boolean(done[note.id])} data-testid="addin-review-note">
                <Kind>
                  {itemLabel(note)} · {NOTE_KINDS[note.kind] || "Note"} ·{" "}
                  {note.paragraphs.map((p) => (
                    <Ref key={p} type="button" onClick={() => goTo(p)} title={`Show paragraph ${p} in the document`} data-testid="addin-ref">
                      ¶{p}
                    </Ref>
                  ))}
                </Kind>
                <strong style={{ fontSize: 12 }}>{note.title}</strong>
                <Small style={{ color: "#334155" }}>
                  <Linked text={note.note} onRef={goTo} />
                </Small>
                <Actions>
                  {done[note.id] ? (
                    <Small>Comment added ✓</Small>
                  ) : (
                    <>
                      <Primary type="button" onClick={() => comment(note)} disabled={locked} data-testid="addin-note-comment">
                        Add comment
                      </Primary>
                      <Button type="button" onClick={() => goTo(note.anchor)}>
                        Show
                      </Button>
                      <Button type="button" onClick={() => dismiss(note.id)}>
                        Dismiss
                      </Button>
                    </>
                  )}
                </Actions>
              </Card>
            ))}
          </Section>

          <Section>
            <SectionTitle>Line edits</SectionTitle>
            <Chips>
              <Chip type="button" $active={kind === "all"} onClick={() => setKind("all")}>
                All ({(review.edits || []).length})
              </Chip>
              {Object.entries(EDIT_KINDS).map(([key, label]) =>
                counts[key] ? (
                  <Chip key={key} type="button" $active={kind === key} onClick={() => setKind(key)} data-testid={`addin-edit-filter-${key}`}>
                    {label} ({counts[key]})
                  </Chip>
                ) : null
              )}
            </Chips>
            {edits.length === 0 && <Small>No line edits{kind === "all" ? "" : " of this kind"}.</Small>}
            {edits.map((edit) => (
              <Card key={edit.id} $accent={ACCENT[edit.kind]} $done={Boolean(done[edit.id])} data-testid="addin-review-edit">
                <Kind>
                  {itemLabel(edit)} · {EDIT_KINDS[edit.kind] || "Edit"} ·{" "}
                  <Ref type="button" onClick={() => showEdit(edit)} title="Show these words in the document" data-testid="addin-ref">
                    ¶{edit.paragraph}
                  </Ref>
                </Kind>
                <Was>{edit.original}</Was>
                <Now>{edit.replacement}</Now>
                {edit.why && <Small>{edit.why}</Small>}
                <Actions>
                  {done[edit.id] ? (
                    <Small>Applied as a tracked change ✓</Small>
                  ) : (
                    <>
                      <Primary type="button" onClick={() => apply(edit)} disabled={locked} data-testid="addin-edit-apply">
                        {busyId === edit.id ? "Applying…" : "Apply"}
                      </Primary>
                      <Button type="button" onClick={() => showEdit(edit)} data-testid="addin-edit-show">
                        Show
                      </Button>
                      <Button type="button" onClick={() => dismiss(edit.id)} data-testid="addin-edit-dismiss">
                        Dismiss
                      </Button>
                    </>
                  )}
                </Actions>
              </Card>
            ))}
          </Section>
        </>
      )}
    </>
  );
};

export default BriefReviewTab;
